import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Radio,
  RadioGroup,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Alert,
  CircularProgress,
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { createPatient } from '../features/patients/patientSlice';

const PatientIntakeForm = ({ onSuccess }) => {
  const dispatch = useDispatch();
  const { loading, error } = useSelector((state) => state.patients);
  const [activeStep, setActiveStep] = useState(0);
  const [formData, setFormData] = useState({
    // Personal Information
    firstName: '',
    lastName: '',
    dateOfBirth: '',
    gender: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    occupation: '',
    maritalStatus: '',
    emergencyContact: {
      name: '',
      relationship: '',
      phone: '',
      email: '',
      address: '',
    },

    // Medical History
    medicalConditions: [],
    allergies: [],
    medications: [],
    surgeries: [],
    familyHistory: {
      conditions: [],
      notes: '',
    },

    // Mental Health History
    previousTreatment: false,
    previousTreatmentDetails: {
      type: '',
      duration: '',
      provider: '',
      outcome: '',
    },
    currentSymptoms: [],
    symptomDuration: '',
    suicidalIdeation: false,
    suicidalDetails: '',
    selfHarm: false,
    selfHarmDetails: '',
    substanceUse: false,
    substanceDetails: {
      type: '',
      frequency: '',
      duration: '',
      notes: '',
    },

    // Insurance Information
    insuranceProvider: '',
    insuranceId: '',
    groupNumber: '',
    policyHolder: {
      name: '',
      relationship: '',
      dateOfBirth: '',
    },

    // Additional Information
    preferredLanguage: '',
    culturalConsiderations: '',
    specialNeeds: '',
    notes: '',
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleEmergencyContactChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      emergencyContact: {
        ...prev.emergencyContact,
        [name]: value,
      },
    }));
  };

  const handleFamilyHistoryChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      familyHistory: {
        ...prev.familyHistory,
        [name]: value,
      },
    }));
  };

  const handleSubstanceDetailsChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      substanceDetails: {
        ...prev.substanceDetails,
        [name]: value,
      },
    }));
  };

  const handlePolicyHolderChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      policyHolder: {
        ...prev.policyHolder,
        [name]: value,
      },
    }));
  };

  const handleSubmit = async () => {
    try {
      await dispatch(createPatient({ patientData: formData })).unwrap();
      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      console.error('Failed to create patient:', error);
    }
  };

  const steps = [
    {
      label: 'Personal Information',
      content: (
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label='First Name'
              name='firstName'
              value={formData.firstName}
              onChange={handleChange}
              required
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label='Last Name'
              name='lastName'
              value={formData.lastName}
              onChange={handleChange}
              required
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              type='date'
              label='Date of Birth'
              name='dateOfBirth'
              value={formData.dateOfBirth}
              onChange={handleChange}
              InputLabelProps={{ shrink: true }}
              required
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth required>
              <InputLabel>Gender</InputLabel>
              <Select
                name='gender'
                value={formData.gender}
                onChange={handleChange}
                label='Gender'
              >
                <MenuItem value='male'>Male</MenuItem>
                <MenuItem value='female'>Female</MenuItem>
                <MenuItem value='other'>Other</MenuItem>
                <MenuItem value='prefer_not_to_say'>Prefer not to say</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label='Email'
              name='email'
              type='email'
              value={formData.email}
              onChange={handleChange}
              required
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label='Phone'
              name='phone'
              value={formData.phone}
              onChange={handleChange}
              required
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label='Address'
              name='address'
              value={formData.address}
              onChange={handleChange}
              required
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              label='City'
              name='city'
              value={formData.city}
              onChange={handleChange}
              required
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              label='State'
              name='state'
              value={formData.state}
              onChange={handleChange}
              required
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              label='ZIP Code'
              name='zipCode'
              value={formData.zipCode}
              onChange={handleChange}
              required
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label='Occupation'
              name='occupation'
              value={formData.occupation}
              onChange={handleChange}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Marital Status</InputLabel>
              <Select
                name='maritalStatus'
                value={formData.maritalStatus}
                onChange={handleChange}
                label='Marital Status'
              >
                <MenuItem value='single'>Single</MenuItem>
                <MenuItem value='married'>Married</MenuItem>
                <MenuItem value='divorced'>Divorced</MenuItem>
                <MenuItem value='widowed'>Widowed</MenuItem>
                <MenuItem value='other'>Other</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      ),
    },
    {
      label: 'Emergency Contact',
      content: (
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label='Emergency Contact Name'
              name='name'
              value={formData.emergencyContact.name}
              onChange={handleEmergencyContactChange}
              required
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label='Relationship'
              name='relationship'
              value={formData.emergencyContact.relationship}
              onChange={handleEmergencyContactChange}
              required
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label='Emergency Contact Phone'
              name='phone'
              value={formData.emergencyContact.phone}
              onChange={handleEmergencyContactChange}
              required
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label='Emergency Contact Email'
              name='email'
              type='email'
              value={formData.emergencyContact.email}
              onChange={handleEmergencyContactChange}
              required
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label='Emergency Contact Address'
              name='address'
              value={formData.emergencyContact.address}
              onChange={handleEmergencyContactChange}
              required
            />
          </Grid>
        </Grid>
      ),
    },
    {
      label: 'Medical History',
      content: (
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              rows={3}
              label='Medical Conditions'
              name='medicalConditions'
              value={formData.medicalConditions.join(', ')}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  medicalConditions: e.target.value
                    .split(',')
                    .map((item) => item.trim()),
                }))
              }
              helperText='Separate conditions with commas'
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              rows={3}
              label='Allergies'
              name='allergies'
              value={formData.allergies.join(', ')}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  allergies: e.target.value
                    .split(',')
                    .map((item) => item.trim()),
                }))
              }
              helperText='Separate allergies with commas'
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              rows={3}
              label='Current Medications'
              name='medications'
              value={formData.medications.join(', ')}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  medications: e.target.value
                    .split(',')
                    .map((item) => item.trim()),
                }))
              }
              helperText='Separate medications with commas'
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              rows={3}
              label='Previous Surgeries'
              name='surgeries'
              value={formData.surgeries.join(', ')}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  surgeries: e.target.value
                    .split(',')
                    .map((item) => item.trim()),
                }))
              }
              helperText='Separate surgeries with commas'
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              rows={3}
              label='Family Medical History'
              name='conditions'
              value={formData.familyHistory.conditions.join(', ')}
              onChange={(e) =>
                handleFamilyHistoryChange({
                  target: {
                    name: 'conditions',
                    value: e.target.value.split(',').map((item) => item.trim()),
                  },
                })
              }
              helperText='Separate conditions with commas'
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              rows={3}
              label='Family History Notes'
              name='notes'
              value={formData.familyHistory.notes}
              onChange={handleFamilyHistoryChange}
            />
          </Grid>
        </Grid>
      ),
    },
    {
      label: 'Mental Health History',
      content: (
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.previousTreatment}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      previousTreatment: e.target.checked,
                    }))
                  }
                />
              }
              label='Previous Mental Health Treatment'
            />
          </Grid>
          {formData.previousTreatment && (
            <>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label='Treatment Type'
                  name='type'
                  value={formData.previousTreatmentDetails.type}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      previousTreatmentDetails: {
                        ...prev.previousTreatmentDetails,
                        type: e.target.value,
                      },
                    }))
                  }
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label='Duration'
                  name='duration'
                  value={formData.previousTreatmentDetails.duration}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      previousTreatmentDetails: {
                        ...prev.previousTreatmentDetails,
                        duration: e.target.value,
                      },
                    }))
                  }
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label='Provider'
                  name='provider'
                  value={formData.previousTreatmentDetails.provider}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      previousTreatmentDetails: {
                        ...prev.previousTreatmentDetails,
                        provider: e.target.value,
                      },
                    }))
                  }
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label='Outcome'
                  name='outcome'
                  value={formData.previousTreatmentDetails.outcome}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      previousTreatmentDetails: {
                        ...prev.previousTreatmentDetails,
                        outcome: e.target.value,
                      },
                    }))
                  }
                />
              </Grid>
            </>
          )}
          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              rows={3}
              label='Current Symptoms'
              name='currentSymptoms'
              value={formData.currentSymptoms.join(', ')}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  currentSymptoms: e.target.value
                    .split(',')
                    .map((item) => item.trim()),
                }))
              }
              helperText='Separate symptoms with commas'
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label='Symptom Duration'
              name='symptomDuration'
              value={formData.symptomDuration}
              onChange={handleChange}
            />
          </Grid>
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.suicidalIdeation}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      suicidalIdeation: e.target.checked,
                    }))
                  }
                />
              }
              label='Suicidal Ideation'
            />
          </Grid>
          {formData.suicidalIdeation && (
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={2}
                label='Suicidal Ideation Details'
                name='suicidalDetails'
                value={formData.suicidalDetails}
                onChange={handleChange}
              />
            </Grid>
          )}
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.selfHarm}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      selfHarm: e.target.checked,
                    }))
                  }
                />
              }
              label='Self-Harm'
            />
          </Grid>
          {formData.selfHarm && (
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={2}
                label='Self-Harm Details'
                name='selfHarmDetails'
                value={formData.selfHarmDetails}
                onChange={handleChange}
              />
            </Grid>
          )}
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.substanceUse}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      substanceUse: e.target.checked,
                    }))
                  }
                />
              }
              label='Substance Use'
            />
          </Grid>
          {formData.substanceUse && (
            <>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label='Substance Type'
                  name='type'
                  value={formData.substanceDetails.type}
                  onChange={handleSubstanceDetailsChange}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label='Frequency'
                  name='frequency'
                  value={formData.substanceDetails.frequency}
                  onChange={handleSubstanceDetailsChange}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label='Duration'
                  name='duration'
                  value={formData.substanceDetails.duration}
                  onChange={handleSubstanceDetailsChange}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={2}
                  label='Notes'
                  name='notes'
                  value={formData.substanceDetails.notes}
                  onChange={handleSubstanceDetailsChange}
                />
              </Grid>
            </>
          )}
        </Grid>
      ),
    },
    {
      label: 'Insurance Information',
      content: (
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label='Insurance Provider'
              name='insuranceProvider'
              value={formData.insuranceProvider}
              onChange={handleChange}
              required
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label='Insurance ID'
              name='insuranceId'
              value={formData.insuranceId}
              onChange={handleChange}
              required
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label='Group Number'
              name='groupNumber'
              value={formData.groupNumber}
              onChange={handleChange}
            />
          </Grid>
          <Grid item xs={12}>
            <Typography variant='subtitle1' gutterBottom>
              Policy Holder Information
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label='Policy Holder Name'
              name='name'
              value={formData.policyHolder.name}
              onChange={handlePolicyHolderChange}
              required
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label='Relationship to Patient'
              name='relationship'
              value={formData.policyHolder.relationship}
              onChange={handlePolicyHolderChange}
              required
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              type='date'
              label='Policy Holder Date of Birth'
              name='dateOfBirth'
              value={formData.policyHolder.dateOfBirth}
              onChange={handlePolicyHolderChange}
              InputLabelProps={{ shrink: true }}
              required
            />
          </Grid>
        </Grid>
      ),
    },
    {
      label: 'Additional Information',
      content: (
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label='Preferred Language'
              name='preferredLanguage'
              value={formData.preferredLanguage}
              onChange={handleChange}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              rows={3}
              label='Cultural Considerations'
              name='culturalConsiderations'
              value={formData.culturalConsiderations}
              onChange={handleChange}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              rows={3}
              label='Special Needs'
              name='specialNeeds'
              value={formData.specialNeeds}
              onChange={handleChange}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              rows={4}
              label='Additional Notes'
              name='notes'
              value={formData.notes}
              onChange={handleChange}
            />
          </Grid>
        </Grid>
      ),
    },
  ];

  return (
    <Box>
      <Typography variant='h5' gutterBottom>
        New Patient Intake Form
      </Typography>

      {error && (
        <Alert severity='error' sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 3 }}>
        <Stepper activeStep={activeStep} orientation='vertical'>
          {steps.map((step, index) => (
            <Step key={step.label}>
              <StepLabel>{step.label}</StepLabel>
              <StepContent>
                {step.content}
                <Box sx={{ mb: 2, mt: 2 }}>
                  <div>
                    <Button
                      variant='contained'
                      onClick={() => setActiveStep((prevStep) => prevStep + 1)}
                      sx={{ mt: 1, mr: 1 }}
                      disabled={activeStep === steps.length - 1}
                    >
                      {index === steps.length - 1 ? 'Finish' : 'Continue'}
                    </Button>
                    <Button
                      disabled={index === 0}
                      onClick={() => setActiveStep((prevStep) => prevStep - 1)}
                      sx={{ mt: 1, mr: 1 }}
                    >
                      Back
                    </Button>
                  </div>
                </Box>
              </StepContent>
            </Step>
          ))}
        </Stepper>

        {activeStep === steps.length && (
          <Box sx={{ mt: 3 }}>
            <Button
              onClick={handleSubmit}
              variant='contained'
              disabled={loading}
            >
              {loading ? <CircularProgress size={24} /> : 'Submit'}
            </Button>
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default PatientIntakeForm;
